import { Subscription } from 'rxjs';
import { SapiService } from './../../../services/sapi.service';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RealtimeService } from './../../../services/realtime.service';
import { Viewer } from './models/viewer.model';

@Component({
	selector: 'realtime-audience-map',
	styleUrls: ['./realtime-audience-map.component.scss'],
	templateUrl: './realtime-audience-map.component.html',
})

export class RealtimeAudienceMapComponent implements OnInit, OnDestroy {
	@ViewChild('gmap', { static: true }) gmapElement: any;

	lockClasses = ['fa', 'fa-unlock'];

	private map: google.maps.Map;
	private viewersOnMap: Viewer[];
	private viewersOnMapCount = 0;
	private autoZoom = true;
	private zoomLock = false;
	private subViewerAdded: Subscription;
	private subViewerRemoved: Subscription;
	private zoomTimeout: any;

	private mapIconInit = '/resources/img/realtime/maps/PushpinInit.svg';
	private mapIconPlaying = '/resources/img/realtime/maps/PushpinPlaying.svg';
	private mapIconPaused = '/resources/img/realtime/maps/PushpinPaused.svg';
	private mapIconFinished = '/resources/img/realtime/maps/PushpinFinished.svg';
	private mapIconAd = '/resources/img/realtime/maps/PushpinAd.svg';

	private mapsEventListenerOnMousOver: google.maps.MapsEventListener;
	private mapsEventListenerOnMousOut: google.maps.MapsEventListener;
	private mapsEventListenerOnZoomChanged: google.maps.MapsEventListener;
	private mapsEventListenerUpdateMapBounds: google.maps.MapsEventListener;

	private viewerSubscriptions: Map<string, Subscription>;

	constructor(protected realtimeService: RealtimeService, protected sapiService: SapiService) {

	}

	ngOnInit() {
		this.checkMapsReady();
	}

	// edge case where index.html did not load maps.googleapis yet.
	private checkMapsReady(): void {
		if (!(window as any).googleMapsReady) {
			setTimeout(() => {
				this.checkMapsReady();
			}, 1000);
		} else {
			this.initWhenMapReady();
		}
	}

	private initWhenMapReady(): void {
		const mapProp = {
			zoom: 2,
			center: new google.maps.LatLng(52.087523, 5.109913),
			mapTypeId: google.maps.MapTypeId.ROADMAP,
			disableDefaultUI: true,
			zoomControl: true,
			fullscreenControl: true,
			minZoom: 0.7,
			maxZoom: 10
		};
		this.viewersOnMap = new Array<Viewer>();
		this.viewerSubscriptions = new Map<string, Subscription>();

		this.map = new google.maps.Map(this.gmapElement.nativeElement, mapProp);

		this.mapsEventListenerOnMousOver = this.map.addListener('mouseover', () => {
			this.stopAutoZoom();
			clearTimeout(this.zoomTimeout);
		});
		this.mapsEventListenerOnMousOut = this.map.addListener('mouseout', () => {
			this.zoomTimeout = setTimeout(() => {
				this.zoomMapToAllViewers();
				this.startAutoZoom();
			}, 6000);
		});
		this.mapsEventListenerOnZoomChanged = this.map.addListener('zoom_changed', () => {
			clearTimeout(this.zoomTimeout);
		});

		this.mapsEventListenerUpdateMapBounds = this.map.addListener('center_changed', () => {
			// later maybe?
		});

		this.subViewerAdded = this.realtimeService.viewerAdded.subscribe((viewer: Viewer) => {

			if (this.viewersOnMap.indexOf(viewer) === -1 && viewer.latLong) {
				this.viewersOnMap.push(viewer);
				const marker: google.maps.Marker = this.createMarker(viewer);
				viewer.marker = marker;
				this.changeMarker(marker, 'init');

				if (this.autoZoom && this.viewersOnMap.length > this.viewersOnMapCount) {
					this.zoomMapToAllViewers();
				}
				this.viewersOnMapCount = this.viewersOnMap.length;

				const sub: Subscription = viewer.playStateChanged.subscribe((playState) => {
					this.changeMarker(viewer.marker, playState);
				});

				this.viewerSubscriptions.set(viewer.uid, sub);
			}
		});

		this.subViewerRemoved = this.realtimeService.viewerRemoved.subscribe((viewer: Viewer) => {
			if (viewer.marker) {
				viewer.marker.setMap(null);
				viewer.marker = null;
			}

			const cnt = this.viewersOnMap.length;
			for (let i = 0; i < cnt; i++) {
				const v: Viewer = this.viewersOnMap[i];
				if (v === viewer) {
					this.viewersOnMap.splice(i, 1);
					this.viewersOnMapCount = this.viewersOnMap.length;
					this.unsubscribeToViewer(v);
					return;
				}
			}
		});
	}

	ngOnDestroy() {
		this.subViewerAdded.unsubscribe();
		this.subViewerRemoved.unsubscribe();

		this.mapsEventListenerOnMousOver.remove();
		this.mapsEventListenerOnMousOut.remove();
		this.mapsEventListenerOnZoomChanged.remove();
		this.mapsEventListenerUpdateMapBounds.remove();

		for (let i = 0; i < this.viewersOnMap.length; i++) {
			const v: Viewer = this.viewersOnMap[i];
			this.unsubscribeToViewer(v);
		}

		this.viewersOnMap.splice(0, this.viewersOnMap.length);
	}

	public toggleLock(): void {
		this.zoomLock = !this.zoomLock;
		// console.log(this.zoomLock);
		if (this.zoomLock) {
			this.lockClasses = ['fa', 'fa-lock'];
		} else {
			this.lockClasses = ['fa', 'fa-unlock'];
		}
	}

	private unsubscribeToViewer(v: Viewer): void {
		const sub: Subscription = this.viewerSubscriptions.get(v.uid);
		if (sub) {
			sub.unsubscribe();
		}
		this.viewerSubscriptions.delete(v.uid);
	}

	private changeMarker(marker: google.maps.Marker, playState: string): void {
		if (marker) {
			switch (playState) {
				case 'init':
					marker.setIcon(this.mapIconInit);
					break;
				case 'playing':
					marker.setIcon(this.mapIconPlaying);
					break;
				case 'paused':
					marker.setIcon(this.mapIconPaused);
					break;
				case 'finished':
					marker.setIcon(this.mapIconFinished);
					break;
				case 'ad':
					marker.setIcon(this.mapIconAd);
					break;
			}
		}
	}

	private createMarker(viewer: Viewer): google.maps.Marker {
		let marker: google.maps.Marker;

		if (viewer && viewer.latLong) {
			if (this.map != null) {
				marker = new google.maps.Marker({
					position: viewer.latLong,
					map: this.map,
					animation: google.maps.Animation.DROP,
					title: ''
				});
				marker.addListener('click', () => {
					viewer.pinned = !viewer.pinned;
				});
			}
		}
		return marker;
	}

	private zoomMapToAllViewers(): void {
		if (this.zoomLock || !this.viewersOnMap) {
			return;
		}

		const mapBounds = new google.maps.LatLngBounds();
		const cnt = this.viewersOnMap.length;

		let viewer: Viewer;
		for (let i = 0; i < cnt; i++) {
			viewer = this.viewersOnMap[i];
			if (viewer.latLong) {
				mapBounds.extend(viewer.latLong);
			}
		}

		this.map.fitBounds(mapBounds);
	}

	private stopAutoZoom(): void {
		this.autoZoom = false;
	}
	private startAutoZoom(): void {
		this.autoZoom = true;
	}
}
